import { type DatasourceParameters } from '@atlaskit/linking-types';
import { getJiraServers } from '@databases/database-state';
import {
  CloudId,
  JiraColumn,
  JiraIssueRef,
  JiraServer,
  JiraServerId,
  ResolvedJiraIssueRef,
} from '@databases/types/confluence';
import { uniq } from 'lodash-es';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import {
  getCloudIdFromServerId,
  getServerIdFromCloudId,
} from '@canvas/atlassian-service/datasource/migration';

import {
  ResolvedJiraIssuesPage,
  getJiraIssuesByQuery,
  getJiraIssuesFromDatasourceByJql,
  getRecentJiraIssues,
  requiredJiraColumns,
  resolveJiraIssue,
} from '../../confluence/jira';
import { serializeError } from '../../utils';
import { CommonRootState, loadAsync } from '../redux';

import {
  selectJiraIssueColumns,
  selectJiraIssueOptional,
  selectJiraIssueMetadata,
  selectJiraServers,
  selectJiraServersStatus,
} from './selectors';
import jiraSlice from './slice';

type Dispatcher<Result> = ThunkAction<Result, CommonRootState, void, AnyAction>;

/**
 * Load the specified Jira issue into the store. If the Jira issue is already loaded with the necessary columns, no request is made.
 * The specified columns are persisted and will be fetched for all future issues.
 */
export const loadJiraIssue =
  (
    ref: JiraIssueRef,
    {
      forceReload = false,
      columns = [],
    }: {
      forceReload?: boolean;
      columns?: string[];
    } = {}
  ): Dispatcher<Promise<ResolvedJiraIssueRef | undefined>> =>
  async (dispatch, getState) => {
    if (ref.serverId && !ref.cloudId) {
      ref.cloudId = await getCloudIdFromServerId(ref.serverId);
    }
    if (!ref.serverId && ref.cloudId) {
      ref.serverId = await getServerIdFromCloudId(ref.cloudId);
    }

    const currentColumns = selectJiraIssueColumns(getState(), {
      cloudId: ref.cloudId,
      serverId: ref.serverId,
    });
    if (!columns.every((c) => currentColumns.includes(c))) {
      dispatch(
        jiraSlice.actions.addColumns({
          cloudId: ref.cloudId,
          serverId: ref.serverId,
          columns,
        })
      );
    }

    const loadedIssue = selectJiraIssueOptional(getState(), ref);
    const loadedColumns = loadedIssue ? Object.keys(loadedIssue.columns) : [];
    const needsReload = !columns.every((column) => loadedColumns.includes(column));

    await loadAsync(
      () => selectJiraIssueMetadata(getState(), ref),
      () => {
        dispatch(jiraSlice.actions.loadJiraIssuePending(ref));
      },
      () =>
        resolveJiraIssue(
          ref,
          uniq([
            ...requiredJiraColumns,
            ...selectJiraIssueColumns(getState(), {
              cloudId: ref.cloudId,
              serverId: ref.serverId,
            }),
          ])
        ),
      async (authorization) => {
        if (authorization) {
          if (authorization.authorized) {
            dispatch(addJiraIssuesToList(authorization.data));
          }

          dispatch(
            jiraSlice.actions.loadJiraIssueFinished({
              ref,
              access: authorization.access,
            })
          );
        }
      },
      (error) => {
        dispatch(
          jiraSlice.actions.loadJiraIssueError({ ...ref, error: serializeError(error) })
        );
      },
      {
        forceReload: forceReload || needsReload,
      }
    );

    return selectJiraIssueOptional(getState(), ref);
  };

/**
 * Add new Jira issues to the list of issues. If an issue with the same key is already loaded, it is replaced.
 */
export const addJiraIssuesToList = (issues: ResolvedJiraIssueRef[]): AnyAction =>
  jiraSlice.actions.addJiraIssuesToList({
    issues,
  });

/**
 * Delete a Jira issue from the list of issues.
 */
export const deleteJiraIssueFromList = (issue: JiraIssueRef): AnyAction =>
  deleteJiraIssuesFromList([issue]);

export const deleteJiraIssuesFromList = (issues: JiraIssueRef[]): AnyAction =>
  jiraSlice.actions.deleteJiraIssuesFromList({ issues });

/**
 * Searches for Jira issues using the specified query and adds the results to the Jira issues state.
 */
export const searchJiraIssues =
  (
    cloudId: CloudId,
    serverId: JiraServerId,
    query: string
  ): Dispatcher<Promise<ResolvedJiraIssueRef[]>> =>
  async (dispatch, getState) => {
    if (serverId && !cloudId) {
      cloudId = await getCloudIdFromServerId(serverId);
    }
    const columns = uniq([
      ...requiredJiraColumns,
      ...selectJiraIssueColumns(getState(), { cloudId, serverId }),
    ]);
    const results =
      query.trim() === ''
        ? await getRecentJiraIssues(cloudId, serverId, columns)
        : await getJiraIssuesByQuery(cloudId, serverId, query, columns);
    dispatch(addJiraIssuesToList(results));
    return results;
  };

export const loadJiraIssuesFromJql =
  (
    cloudId: CloudId,
    parameters: DatasourceParameters,
    columns: JiraColumn[] = [],
    pageSize: number,
    pageCursor: string
  ): Dispatcher<Promise<ResolvedJiraIssuesPage>> =>
  async (dispatch) => {
    const response = await getJiraIssuesFromDatasourceByJql(
      cloudId,
      parameters,
      columns,
      pageSize,
      pageCursor
    );

    if (response.authorized) {
      dispatch(addJiraIssuesToList(response.data.issues));
      return response.data;
    }

    return { issues: [], nextPageCursor: undefined, totalCount: undefined };
  };

export const loadJiraServers =
  (): Dispatcher<Promise<JiraServer[] | undefined>> => async (dispatch, getState) => {
    await loadAsync(
      () => selectJiraServersStatus(getState()),
      () => {
        dispatch(jiraSlice.actions.loadJiraServersPending());
      },
      async () => await getJiraServers(),
      async (applicationLinks) => {
        dispatch(jiraSlice.actions.loadJiraServersFinished(applicationLinks));
      },
      (error) => {
        dispatch(
          jiraSlice.actions.loadJiraServersError({ error: serializeError(error) })
        );
      }
    );

    return selectJiraServers(getState());
  };

export const resetJiraServerIssues = (
  cloudId: CloudId,
  serverId: JiraServerId
): AnyAction => jiraSlice.actions.resetJiraServerIssues({ cloudId, serverId });
